import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import moment, { Moment } from 'moment'

import { Avatar, Div, H1 } from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'

import { fetchDeals, fetchDealTotals } from 'api'
import { EngagementScoreFlames } from 'components/EngagementScoreFlames'
import { CurrentUserContext } from 'context/currentUserContext'
import { OrgUsersContext } from 'context/orgUsersContext'
import { SortContext } from 'context/sortContext'
import { AccountActivitySortValue, InsightOpportunity } from 'modules/accountActivity/domain'
import { formatDollars } from 'utils/formatNumbers'
import { accountPath } from 'utils/paths'

import DealsFilter from './DealsFilter'
import TableBody from './TableBody'

import styles from './styles.module.css'

const tableHeaders = [
  {
    label: 'Name',
    sortable: true,
    sortValue: AccountActivitySortValue.DEAL_NAME,
    initialDescending: false,
    width: 240
  },
  {
    label: 'Engagement Score',
    sortable: true,
    sortValue: AccountActivitySortValue.ENGAGEMENT_SCORE,
    initialDescending: true
  },
  {
    label: 'Owner',
    sortable: false,
    width: 160
  },
  {
    label: 'Total ARR',
    sortable: true,
    sortValue: AccountActivitySortValue.DEAL_AMOUNT,
    initialDescending: true,
    width: 60
  },
  {
    label: 'Close date',
    sortable: true,
    sortValue: AccountActivitySortValue.MOST_RECENT,
    initialDescending: true,
    width: 140
  },
  {
    label: 'Forecast category',
    sortable: true,
    sortValue: AccountActivitySortValue.DEAL_FORECAST_CATEGORY,
    initialDescending: false,
    width: 160
  },
  {
    label: 'Stage name',
    sortable: true,
    sortValue: AccountActivitySortValue.DEAL_STAGE,
    initialDescending: false,
    width: 120
  },
  {
    label: 'Type',
    sortable: true,
    sortValue: AccountActivitySortValue.DEAL_TYPE,
    initialDescending: false
  },
  {
    label: 'Probability',
    sortable: true,
    sortValue: AccountActivitySortValue.DEAL_PROBABILITY,
    initialDescending: true
  }
]

const formatDate = (date: Moment) => {
  if (date.year() === moment().year()) {
    return 'Q' + date.format('Q   MMM D')
  } else {
    return 'Q' + date.format('Q   MMM D YYYY')
  }
}

const DealsPage: React.FC<{}> = () => {
  const [deals, setDeals] = useState<InsightOpportunity[]>([])
  const [totalDeals, setTotalDeals] = useState<number>()
  const [totalDealAmount, setTotalDealAmount] = useState<number>()
  const [isLoadingDeals, setIsLoadingDeals] = useState<boolean>(true)

  const currentUser = useContext(CurrentUserContext)
  const orgUsersById = useContext(OrgUsersContext)
  const { dealsSort, setDealsSort, dealsDescending, setDealsDescending } = useContext(SortContext)

  const [filters, setFilters] = useState({})
  const [search, setSearch] = useState<string>()
  const [dateRange, setDateRange] = useState()

  useEffect(() => {
    if (currentUser && isLoadingDeals) {
      fetchDeals({
        filters,
        search,
        dateRange,
        sortValue: dealsSort,
        sortDescending: dealsDescending
      })
        .then(({ data }) => setDeals(data))
        .finally(() => setIsLoadingDeals(false))
      fetchDealTotals({ filters, search, dateRange }).then(({ count, totalAmount }) => {
        setTotalDeals(count)
        setTotalDealAmount(totalAmount)
      })
    }
  }, [currentUser, filters, isLoadingDeals, dealsSort, dealsDescending, search, dateRange])

  useEffect(() => {
    setIsLoadingDeals(true)
  }, [dealsSort, filters, dealsDescending, search, dateRange])

  function onFilterChange(filterParam: any, filterValue: any) {
    setFilters(prevState => ({
      ...prevState,
      [filterParam]: filterValue
    }))
  }

  return (
    <Div className={styles.DealsPage}>
      <Div className={styles.dealsTable}>
        <Div className={styles.header}>
          <H1>Deals</H1>
        </Div>
        <Div className={styles.TableContainer}>
          <Div className={styles.TotalsAndFilters}>
            {totalDealAmount && totalDeals && (
              <p className={styles.totalDescription}>
                Total of <span>{`${formatDollars(totalDealAmount)} ARR`}</span> across{' '}
                <span>{totalDeals}</span> deals
              </p>
            )}
            <DealsFilter
              onFilterChange={onFilterChange}
              appliedFilters={filters}
              dateRange={dateRange}
              setDateRange={setDateRange}
              searchQuery={search}
              setSearchQuery={setSearch}
            />
          </Div>
          <TableBody
            isEmpty={!isLoadingDeals && deals.length === 0}
            isLoading={isLoadingDeals && !deals}
            table={
              <Table
                columns={tableHeaders.map(header => ({
                  ...header,
                  sorting: header.sortValue === dealsSort,
                  onClick: () => {
                    if (header.sortValue) {
                      setDealsDescending(
                        header.sortValue === dealsSort
                          ? !dealsDescending
                          : header.initialDescending !== false
                      )
                      setDealsSort(header.sortValue)
                    }
                  },
                  direction: dealsDescending ? 'desc' : 'asc'
                }))}
                data={deals.map(deal => ({
                  rowClickEventData: deal,
                  data: [
                    {
                      content: (
                        <div>
                          <p className={styles.dealName}>{deal.name}</p>
                          <Link to={accountPath(deal.accountId)} className={styles.accountName}>
                            {deal.accountName}
                          </Link>
                        </div>
                      )
                    },
                    {
                      content: <EngagementScoreFlames score={deal.score / 10} />
                    },
                    {
                      content: (
                        <div className={styles.ownerInfo}>
                          <Avatar user={orgUsersById[deal.ownerId] || { id: deal.ownerId }} />
                          <p className={styles.ownerName}>
                            {orgUsersById[deal.ownerId]?.name || 'Not a Drift user'}
                          </p>
                        </div>
                      )
                    },
                    {
                      content: deal.amount ? `$${Math.trunc(deal.amount).toLocaleString()}` : '-'
                    },
                    {
                      content: formatDate(moment(deal.closeDate))
                    },
                    {
                      content: deal.forecastCategoryName
                    },
                    {
                      content: <p>{deal.stageName}</p>
                    },
                    {
                      content: deal.type
                    },
                    {
                      content: `${deal.probability}%`
                    }
                  ]
                }))}
              />
            }
            error={false}
          />
        </Div>
      </Div>
    </Div>
  )
}

export default DealsPage
